import { useSyncExternalStore } from "react";

/**
 * Which set of books is on the screen: the company's, or what people bought with their own
 * money (D, 12 October 2026: "add another slide button on the top left, then it switches to
 * personal account... don't mix the both"). Inventory and purchases show one or the other,
 * never both together. It is a way of looking, kept in this browser; nothing is changed by
 * switching.
 */
export type Books = "company" | "personal";

const KEY = "ohc.books";
const listeners = new Set<() => void>();

function read(): Books {
  try {
    return window.localStorage.getItem(KEY) === "personal" ? "personal" : "company";
  } catch {
    return "company";
  }
}

/** Mark the page, so the styles can tell which books are on the screen. */
function mark(books: Books): void {
  try {
    document.documentElement.dataset.books = books;
  } catch {
    // No page to mark, in a test say.
  }
}

let current: Books = read();
mark(current);

export function setBooks(books: Books): void {
  current = books;
  mark(books);
  try {
    window.localStorage.setItem(KEY, books);
  } catch {
    // A private window keeps nothing: the choice lasts until the page is reloaded.
  }
  for (const listener of listeners) listener();
}

export function useBooks(): Books {
  return useSyncExternalStore(
    (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    () => current,
  );
}
